/**
 * IdeaScan — the Idea Dashboard: donut hero,
 * Patentability Q&A (New/Inventive/Industrial — yes/unsure/no segmented controls
 * with result panels), Business Viability calculator (retail price, margin %,
 * net profit target → required annual customers; £ formatting), Product
 * Desirability lean canvas, Feasibility 10× hockey-stick graph, per-section
 * "Book idea review" CTAs, floating "Ask expert" FAB, first-completion celebration
 * modal. Prototype source: data-page="idea-scan". Local state; Firestore
 * autosave in Phase 4. British English.
 */
import { Fragment, useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { TopNav } from '../components/TopNav';
import { ProgressRing } from '../components/ProgressRing';
import { Button } from '../components/Button';
import { fmtGBP, fmtNum } from '../lib/format';
import { api } from '../lib/api';
import { useSession } from '../context/SessionContext';
import '../components/SectionBox.css';
import './IdeaScan.css';

type PatAnswer = 'yes' | 'unsure' | 'no' | null;

interface PatState {
  new: PatAnswer;
  inv: PatAnswer;
  ind: PatAnswer;
}

interface DesirabilityState {
  customer: string;
  problem: string;
  alternative: string;
  issue: string;
}

interface PersistedDesirability {
  customer: { id: string; text: string }[];
  problem: { id: string; text: string }[];
  alternatives: { id: string; text: string }[];
  alternativeIssues: { id: string; text: string }[];
}

const EMPTY_DESIRABILITY: DesirabilityState = {
  customer: '',
  problem: '',
  alternative: '',
  issue: '',
};

const DESIRABILITY_FIELDS: Array<{
  key: keyof DesirabilityState;
  storageKey: keyof PersistedDesirability;
  title: string;
  miniLabel: string;
  description: string;
  placeholder: string;
}> = [
  {
    key: 'customer',
    storageKey: 'customer',
    title: 'Main customer',
    miniLabel: 'Customer',
    description: 'Who would be most keen to buy this product?',
    placeholder: 'Eg, Cat owners who use a GPS collar',
  },
  {
    key: 'problem',
    storageKey: 'problem',
    title: 'Their main problem',
    miniLabel: 'Customer Problem',
    description: 'What problem are they experiencing (it could be functional, emotional or social)?',
    placeholder: 'Eg, Worrying the cat is lost long before they notice it is missing',
  },
  {
    key: 'alternative',
    storageKey: 'alternatives',
    title: 'Main alternative',
    miniLabel: 'Alternative',
    description: 'How do they try to solve the problem currently?',
    placeholder: 'Eg, Regularly checking the GPS app to spot if the cat has been still for a while',
  },
  {
    key: 'issue',
    storageKey: 'alternativeIssues',
    title: 'Issue with alternative',
    miniLabel: 'Alternative Issue',
    description: 'What is the main issue with the existing alternative they try to use?',
    placeholder: "Eg, By the time you notice the cat hasn't come back it could be hours since it was last seen",
  },
];

const PAT_QUESTIONS = [
  {
    key: 'new' as const,
    name: 'Is it new?',
    desc: 'It must not have been made publicly available anywhere in the world, for example it must not be described in a publication.',
    label: 'Is your invention new?',
  },
  {
    key: 'inv' as const,
    name: 'Is it inventive?',
    desc: 'It cannot be an obvious change to something that already exists.',
    label: 'Is your invention inventive?',
  },
  {
    key: 'ind' as const,
    name: 'Is it something practical, not just an idea?',
    desc: 'Something that can be made and used, a technical process, or a method of doing something.',
    label: 'Does your invention have industrial application?',
  },
];

const NO_ICON = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={3} strokeLinecap="round">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);
const UNSURE_ICON = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.6} strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 9a3 3 0 1 1 4.6 2.5c-.9.6-1.6 1.3-1.6 2.5" />
    <line x1="12" y1="17.5" x2="12" y2="17.6" />
  </svg>
);
const YES_ICON = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={3} strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

function hasAnswer(value: string): boolean {
  return value.trim().length > 0;
}

function firstAnswer(rows: { text: string }[] | undefined): string {
  return rows?.find((row) => hasAnswer(row.text))?.text ?? '';
}

function fromPersistedDesirability(desirability: PersistedDesirability): DesirabilityState {
  return {
    customer: firstAnswer(desirability.customer),
    problem: firstAnswer(desirability.problem),
    alternative: firstAnswer(desirability.alternatives),
    issue: firstAnswer(desirability.alternativeIssues),
  };
}

function toPersistedDesirability(desirability: DesirabilityState): PersistedDesirability {
  const asRows = (id: string, text: string) => (hasAnswer(text) ? [{ id, text }] : []);

  return {
    customer: asRows('customer', desirability.customer),
    problem: asRows('problem', desirability.problem),
    alternatives: asRows('alternative', desirability.alternative),
    alternativeIssues: asRows('issue', desirability.issue),
  };
}

function GrowthGraph({ year3Target, mini = false }: { year3Target: number | null; mini?: boolean }) {
  const values = year3Target ? [0, year3Target / 100, year3Target / 10, year3Target] : [0, 0, 0, 0];
  const width = 300;
  const height = mini ? 160 : 210;
  const padding = { left: 12, right: 16, top: 26, bottom: 26 };
  const pointsX = values.map((_, index) => padding.left + (index * (width - padding.left - padding.right)) / 3);
  const max = Math.max(...values) || 1;
  const pointsY = values.map((value) => height - padding.bottom - (value / max) * (height - padding.top - padding.bottom));
  const labels = ['0', 'Year 1', 'Year 2', 'Year 3'];

  return (
    <svg className="itk-line-graph" viewBox={`0 0 ${width} ${height}`} role="img" aria-label="Customers per year growth targets to year 3">
      <text className="itk-glbl" x={padding.left} y="14" textAnchor="start">Customers per year</text>
      <line className="itk-axis" x1={padding.left} y1={height - padding.bottom} x2={width - padding.right} y2={height - padding.bottom} />
      <polyline className="itk-plot" points={pointsX.map((pointX, index) => `${pointX},${pointsY[index]}`).join(' ')} />
      {pointsX.map((pointX, index) => {
        const anchor = index === 0 ? 'start' : index === 3 ? 'end' : 'middle';
        const pointY = pointsY[index] ?? height - padding.bottom;
        const value = values[index] ?? 0;
        const valueLabelX = index === 2 ? pointX - 10 : pointX;
        const valueLabelY = index === 2 ? pointY - 12 : pointY - 10;
        const valueLabelAnchor = index === 2 ? 'end' : anchor;
        return (
          <Fragment key={labels[index]}>
            <circle className="itk-pt" cx={pointX} cy={pointY} r="5" />
            <text className="itk-xlbl" x={pointX} y={height - padding.bottom + 16} textAnchor={anchor}>{labels[index]}</text>
            {year3Target && index > 0 && (
              <text className="itk-vlbl" x={valueLabelX} y={valueLabelY} textAnchor={valueLabelAnchor}>{fmtNum(value)}</text>
            )}
          </Fragment>
        );
      })}
    </svg>
  );
}

function patVerdict(answers: PatState): { tone: 'good' | 'warn' | 'bad' | 'none'; title: string; text: string } {
  const all = [answers.new, answers.inv, answers.ind];
  if (all.some((a) => a === null)) {
    return {
      tone: 'none',
      title: 'Answer all three questions',
      text: "You haven't answered all of the questions. Complete each one to see whether your invention could be patentable.",
    };
  }
  if (all.every((a) => a === 'yes')) {
    return {
      tone: 'good',
      title: 'Likely patentable',
      text: 'Your answers suggest your invention could meet the UK patentability criteria. A patent attorney can confirm and help you draft a strong application.',
    };
  }
  if (all.some((a) => a === 'no')) {
    return {
      tone: 'bad',
      title: 'May not be patentable',
      text: 'One or more answers suggest your invention may not meet the UK criteria. An innovation coach can help you explore alternative protection or ways to adapt the idea.',
    };
  }
  return {
    tone: 'warn',
    title: 'Potentially patentable',
    text: 'Some answers are uncertain. Consider doing a worldwide patent search and speaking to an innovation coach to clarify the position before investing further.',
  };
}

export function IdeaScan() {
  const navigate = useNavigate();
  const session = useSession();
  const { celebrations, markCelebration } = session;
  const [pat, setPat] = useState<PatState>({ new: null, inv: null, ind: null });
  const [viaPrice, setViaPrice] = useState<number | null>(null);
  const [viaMargin, setViaMargin] = useState(10);
  const [viaProfit, setViaProfit] = useState<number | null>(null);
  const [priceMode, setPriceMode] = useState<'oneoff' | 'monthly'>('oneoff');
  const [feaY3, setFeaY3] = useState<number | null>(null);
  const [desirability, setDesirability] = useState<DesirabilityState>({ ...EMPTY_DESIRABILITY });
  // Deliberately NOT initialised from session.activeIdeaId: the idea doc must be
  // fetched from the BFF before autosave is enabled. Seeding ideaId from the
  // session lets a failed hydration leave an armed autosave pointing at a real
  // document, which then PUTs empty defaults over it (the data-wipe bug).
  const [ideaId, setIdeaId] = useState<string | null>(null);
  const [ideaName, setIdeaName] = useState<string>('');
  const [loaded, setLoaded] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [hydrated, setHydrated] = useState(false);
  // True only after a genuine user edit (post-hydration). The autosave requires
  // hydrated && dirty so it can never fire on the initial load or after a failed
  // hydration.
  const [dirty, setDirty] = useState(false);
  const [completionOpen, setCompletionOpen] = useState(false);
  const celebrationTriggered = useRef(
    Boolean(celebrations.scan || (typeof localStorage !== 'undefined' && localStorage.getItem('itk_celebrated_scan') === 'true')),
  );
  const completionTimer = useRef<number | null>(null);

  const patResult = patVerdict(pat);

  const closeCompletionModal = useCallback(() => {
    celebrationTriggered.current = true;
    markCelebration('scan');
    if (typeof localStorage !== 'undefined') localStorage.setItem('itk_celebrated_scan', 'true');
    setCompletionOpen(false);
  }, [markCelebration]);

  // ── Load (or create) the active idea on mount ──
  // The Idea Dashboard works on a single "active" idea. On a fresh page load
  // (direct refresh / new tab) the session's activeIdeaId is null until Home's
  // /me hydration runs — so hydrate it from /me first to avoid creating a
  // duplicate empty idea and orphaning the previous one's data. British English.
  //
  // On failure the page shows an error + Retry rather than an empty armed form:
  // rendering the form with empty values AND an enabled autosave is what allowed
  // a failed load to wipe a complete idea.
  const loadIdea = useCallback(async () => {
    setLoadError(false);
    setHydrated(false);
    setDirty(false);
    try {
      let id = session.activeIdeaId;
      // Always check /me to hydrate celebrations flag + activeIdeaId if needed.
      const me = await api.get<{ activeIdeaId: string | null; celebrations?: { scan?: boolean } }>('/api/v1/me');
      if (me.celebrations?.scan) {
        celebrationTriggered.current = true;
        session.markCelebration('scan');
        if (typeof localStorage !== 'undefined') localStorage.setItem('itk_celebrated_scan', 'true');
      }
      if (!id) {
        id = me.activeIdeaId ?? null;
        if (id) session.setActiveIdea(id);
      }
      if (id) {
        const res = await api.get<{
          id: string;
          name: string;
          patentability: { new: PatAnswer; inventive: PatAnswer; industrial: PatAnswer };
          viability: { price: number | null; margin: number; profitTarget: number | null; priceMode: 'oneoff' | 'monthly' };
          feasibility: { year3Target: number | null };
          desirability?: PersistedDesirability;
        }>(`/api/v1/ideas/${id}`);
        setIdeaId(id);
        setIdeaName(res.name ?? 'My idea');
        if (res.patentability) setPat({ new: res.patentability.new, inv: res.patentability.inventive, ind: res.patentability.industrial });
        if (res.viability) {
          setViaPrice(res.viability.price);
          setViaMargin(res.viability.margin);
          setViaProfit(res.viability.profitTarget);
          setPriceMode(res.viability.priceMode);
        }
        if (res.feasibility) setFeaY3(res.feasibility.year3Target);
        if (res.desirability) setDesirability(fromPersistedDesirability(res.desirability));
      } else {
        // No active idea anywhere — create one and pin it as active.
        const created = await api.post<{ id: string; name: string }>('/api/v1/ideas', { name: 'My idea' });
        id = created.id;
        setIdeaId(id);
        setIdeaName(created.name);
        session.setActiveIdea(id);
      }
      setHydrated(true);
      setLoaded(true);
    } catch {
      // Hydration failed — surface a Retry instead of an empty armed form.
      setLoadError(true);
      setLoaded(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    void loadIdea();
  }, [loadIdea]);

  // ── Autosave (debounced) ──
  // Persists patentability + viability + feasibility to the BFF. Requires a
  // successful hydration AND a genuine user edit (markDirty), so it can never
  // fire after a failed load or on the initial population of the form.
  const markDirty = useCallback(() => setDirty(true), []);
  useEffect(() => {
    if (!hydrated || !dirty || !ideaId) return;
    const t = setTimeout(() => {
      api
        .put(`/api/v1/ideas/${ideaId}`, {
          name: ideaName,
          patentability: { new: pat.new, inventive: pat.inv, industrial: pat.ind },
          viability: { price: viaPrice, margin: viaMargin, profitTarget: viaProfit, priceMode },
          feasibility: { year3Target: feaY3 },
          desirability: toPersistedDesirability(desirability),
        })
        .catch(() => {
          // Non-fatal — the user's input stays in local state.
        });
    }, 800);
    return () => clearTimeout(t);
  }, [hydrated, dirty, ideaId, ideaName, pat, viaPrice, viaMargin, viaProfit, priceMode, feaY3, desirability]);

  // ── Viability calculations ──
  const viaCalcs = useMemo(() => {
    if (viaPrice === null || viaPrice <= 0 || viaMargin <= 0 || viaProfit === null || viaProfit <= 0) return null;
    const arpc = priceMode === 'monthly' ? viaPrice * 12 : viaPrice;
    const revenue = viaProfit / (viaMargin / 100);
    const cust = Math.ceil(revenue / arpc);
    return { arpc, revenue, cust };
  }, [viaPrice, viaMargin, viaProfit, priceMode]);

  const effectiveY3 = feaY3 && feaY3 > 0 ? feaY3 : viaCalcs?.cust ?? null;

  const score = useMemo(() => {
    let complete = 0;
    if (pat.new) complete += 1;
    if (pat.inv) complete += 1;
    if (pat.ind) complete += 1;
    if (viaProfit !== null && viaProfit > 0) complete += 1;
    if (viaPrice !== null && viaPrice > 0) complete += 1;
    DESIRABILITY_FIELDS.forEach((field) => {
      if (hasAnswer(desirability[field.key])) complete += 1;
    });
    if (effectiveY3) complete += 1;
    return Math.round((complete / 10) * 100);
  }, [pat, viaPrice, viaProfit, desirability, effectiveY3]);

  useEffect(() => {
    if (
      !loaded ||
      score < 100 ||
      celebrations.scan ||
      celebrationTriggered.current ||
      (typeof localStorage !== 'undefined' && localStorage.getItem('itk_celebrated_scan') === 'true')
    ) {
      return;
    }

    celebrationTriggered.current = true;
    markCelebration('scan');
    if (typeof localStorage !== 'undefined') localStorage.setItem('itk_celebrated_scan', 'true');
    api.post('/api/v1/me/celebrations', { scan: true }).catch(() => {});
    completionTimer.current = window.setTimeout(() => setCompletionOpen(true), 420);
  }, [celebrations.scan, loaded, markCelebration, score]);

  useEffect(() => () => {
    if (completionTimer.current !== null) window.clearTimeout(completionTimer.current);
  }, []);

  useEffect(() => {
    if (!completionOpen) return;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') closeCompletionModal();
    };
    document.addEventListener('keydown', closeOnEscape);
    return () => document.removeEventListener('keydown', closeOnEscape);
  }, [closeCompletionModal, completionOpen]);

  return (
    <>
      <TopNav title="Idea Dashboard" onBack={() => navigate('/')} crumb={`${score}%`} />

      <div className="itk-wrap itk-wrap--idea">
        {loadError && (
          <div className="itk-section-box" role="alert" style={{ marginBottom: 16 }}>
            <div className="itk-overline">Idea Dashboard</div>
            <h2 className="itk-sec-title">We couldn&rsquo;t load your idea</h2>
            <p className="itk-sec-desc" style={{ marginBottom: 18 }}>
              Your saved answers are safe — we just couldn&rsquo;t reach them. Check your connection
              and try again.
            </p>
            <Button pill onClick={() => void loadIdea()}>
              Retry
            </Button>
          </div>
        )}
        <div className="itk-dash-zone">
          {/* ── DASHBOARD HERO ── */}
          <section className="itk-section-box" id="itk-dashboard">
            <div className="itk-dash-hero">
              <div>
                <div className="itk-overline">Idea Toolkit</div>
                <h2 className="itk-sec-title">Idea Dashboard Score</h2>
                <p className="itk-sec-desc" style={{ marginBottom: 18 }}>
                  Your score is calculated based on the progress you have made through the idea scan.
                  Complete more sections to improve your score and when you are ready book your free
                  idea review with one of our innovation experts to review your options in more detail.
                </p>
                <Button pill onClick={() => navigate('/book')}>
                  Book idea review
                </Button>
              </div>
              <ProgressRing percent={score} size={172} stroke={16} label="complete" />
            </div>
          </section>

          {/* ── Patentability mini-card ── */}
          <div className="itk-dash-grid">
            <button
              className="itk-dash-card"
              onClick={() => document.getElementById('itk-patentability')?.scrollIntoView({ behavior: 'smooth' })}
              aria-label="Go to Idea Patentability"
            >
              <h3>
                Idea Patentability
                <ArrowIcon />
              </h3>
              <div className="itk-pat-mini">
                {PAT_QUESTIONS.map((q) => (
                  <div key={q.key}>
                    <div className="itk-pat-line-lbl">{q.name}</div>
                    <div className={`itk-pat-track itk-pat-track--${pat[q.key] ?? 'none'}`}>
                      <div className="itk-pat-fill" />
                      <div className="itk-pat-dot">
                        {pat[q.key] === 'yes' ? YES_ICON : pat[q.key] === 'no' ? NO_ICON : pat[q.key] === 'unsure' ? '?' : null}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </button>

            {/* ── Viability mini-card ── */}
            <button
              className="itk-dash-card"
              onClick={() => document.getElementById('itk-viability')?.scrollIntoView({ behavior: 'smooth' })}
              aria-label="Go to Business Viability"
            >
              <h3>
                Business Viability
                <ArrowIcon />
              </h3>
              <div className="itk-via-funnel">
                <div className="itk-via-row" style={{ gridTemplateColumns: '3fr 2fr' }}>
                  <div className="itk-via-step">
                    <span className="itk-via-step-lbl">Profit target</span>
                    <span className="itk-via-step-val">{viaProfit ? fmtGBP(viaProfit) : '—'}</span>
                  </div>
                  <div className="itk-via-step">
                    <span className="itk-via-step-lbl">Profitability</span>
                    <span className="itk-via-step-val">{viaMargin ? `${viaMargin}%` : '—'}</span>
                  </div>
                </div>
                <div className="itk-via-chev">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.4} strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                </div>
                <div className="itk-via-row" style={{ gridTemplateColumns: '2fr 3fr' }}>
                  <div className="itk-via-step">
                    <span className="itk-via-step-lbl">Retail price</span>
                    <span className="itk-via-step-val">{viaPrice ? fmtGBP(viaPrice) : '—'}</span>
                  </div>
                  <div className="itk-via-step">
                    <span className="itk-via-step-lbl">Customers per year</span>
                    <span className="itk-via-step-val">{viaCalcs ? fmtNum(viaCalcs.cust) : '—'}</span>
                  </div>
                </div>
              </div>
            </button>
          </div>

          {/* ── Feasibility + Desirability mini-cards ── */}
          <div className="itk-dash-grid">
            <button
              className="itk-dash-card"
              onClick={() => document.getElementById('itk-feasibility')?.scrollIntoView({ behavior: 'smooth' })}
              aria-label="Go to Project Feasibility"
            >
              <h3>
                Project Feasibility
                <ArrowIcon />
              </h3>
              <div className="itk-fea-mini-graph">
                <GrowthGraph year3Target={effectiveY3} mini />
              </div>
            </button>

            <button
              className="itk-dash-card"
              onClick={() => document.getElementById('itk-desirability')?.scrollIntoView({ behavior: 'smooth' })}
              aria-label="Go to Product Desirability"
            >
              <h3>
                Product Desirability
                <ArrowIcon />
              </h3>
              <div className="itk-des-flow">
                {DESIRABILITY_FIELDS.map((field, index) => {
                  const complete = hasAnswer(desirability[field.key]);
                  return (
                    <Fragment key={field.key}>
                      <div className="itk-des-node">
                        <span className={`itk-des-circ ${complete ? 'itk-des-circ--done' : ''}`.trim()}>
                          {complete ? YES_ICON : '?'}
                        </span>
                        <span className="itk-des-node-lbl">{field.miniLabel}</span>
                      </div>
                      {index < DESIRABILITY_FIELDS.length - 1 && (
                        <span className="itk-des-chev" aria-hidden="true">
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.4} strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="9 18 15 12 9 6" />
                          </svg>
                        </span>
                      )}
                    </Fragment>
                  );
                })}
              </div>
            </button>
          </div>
        </div>

        {/* ── IDEA PATENTABILITY ── */}
        <section className="itk-section-box" id="itk-patentability">
          <div className="itk-overline">Section 1</div>
          <h2 className="itk-sec-title">Idea Patentability</h2>
          <p className="itk-sec-desc">
            Answer three quick questions to find out whether your invention could be patentable in the UK.
          </p>

          {PAT_QUESTIONS.map((q) => (
            <div className="itk-q-item" key={q.key}>
              <div className="itk-q-name">{q.name}</div>
              <p className="itk-q-desc">{q.desc}</p>
              <div className="itk-seg" role="group" aria-label={q.label}>
                {(
                  [
                    { v: 'no', label: 'No', icon: NO_ICON, cls: 'no' },
                    { v: 'unsure', label: 'Not sure', icon: UNSURE_ICON, cls: 'unsure' },
                    { v: 'yes', label: 'Yes', icon: YES_ICON, cls: 'yes' },
                  ] as const
                ).map((opt) => (
                  <button
                    key={opt.v}
                    className={pat[q.key] === opt.v ? `on-${opt.cls}` : ''}
                    onClick={() => { setPat((p) => ({ ...p, [q.key]: opt.v })); markDirty(); }}
                    aria-pressed={pat[q.key] === opt.v}
                    disabled={!hydrated}
                  >
                    {opt.icon}
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          ))}

          <div style={{ marginTop: 4 }}>
            <div className="itk-result-overline">Result</div>
            <div className={`itk-result-box itk-result-box--${patResult.tone}`} aria-live="polite">
              <span className="itk-result-icon">
                {patResult.tone === 'good' ? YES_ICON : patResult.tone === 'bad' ? NO_ICON : '?'}
              </span>
              <div>
                <div className="itk-result-title">{patResult.title}</div>
                <span>{patResult.text}</span>
                {patResult.tone !== 'none' && (
                  <div className="itk-result-actions">
                    <Button pill onClick={() => navigate('/book')}>Book idea review</Button>
                    <Button variant="secondary" pill onClick={() => document.getElementById('itk-viability')?.scrollIntoView({ behavior: 'smooth' })}>
                      Continue to Business Viability
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* ── BUSINESS VIABILITY ── */}
        <section className="itk-section-box" id="itk-viability">
          <div className="itk-overline">Section 2</div>
          <h2 className="itk-sec-title">Business Viability</h2>
          <h3 className="itk-card-title">Profit Calculator</h3>
          <p className="itk-sec-desc">
            Use this calculator to help determine whether your idea could generate enough profit to make
            commercialising it worthwhile.
          </p>

          <div className="itk-two-col">
            <div>
              <div className="itk-col-heading">Inputs</div>
              <div className="itk-field">
                <label className="itk-field-label" htmlFor="itk-inPrice">Retail price</label>
                <div className="itk-input-prefix">
                  <input
                    className="itk-input itk-money"
                    id="itk-inPrice"
                    inputMode="numeric"
                    autoComplete="off"
                    placeholder="e.g. 25"
                    value={viaPrice ? fmtNum(viaPrice) : ''}
                    onChange={(e) => { setViaPrice(parseNum(e.target.value)); markDirty(); }}
                  />
                </div>
                <div className="itk-toggle-pair" role="group" aria-label="Pricing type">
                  <button className={priceMode === 'oneoff' ? 'on' : ''} onClick={() => { setPriceMode('oneoff'); markDirty(); }}>
                    One-off sale
                  </button>
                  <button className={priceMode === 'monthly' ? 'on' : ''} onClick={() => { setPriceMode('monthly'); markDirty(); }}>
                    Monthly Subscription
                  </button>
                </div>
              </div>

              <div className="itk-field">
                <label className="itk-field-label" htmlFor="itk-inMargin">Profitability Target</label>
                <div className="itk-select-wrap">
                  <select
                    className="itk-input"
                    id="itk-inMargin"
                    value={viaMargin}
                    onChange={(e) => { setViaMargin(Number(e.target.value)); markDirty(); }}
                  >
                    {[3, 5, 10, 15, 20, 30].map((m) => (
                      <option key={m} value={m}>{m}%</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="itk-field">
                <label className="itk-field-label" htmlFor="itk-inProfit">Net Profit Target</label>
                <div className="itk-input-prefix">
                  <input
                    className="itk-input itk-money"
                    id="itk-inProfit"
                    inputMode="numeric"
                    autoComplete="off"
                    placeholder="e.g. 50,000"
                    value={viaProfit ? fmtNum(viaProfit) : ''}
                    onChange={(e) => { setViaProfit(parseNum(e.target.value)); markDirty(); }}
                  />
                </div>
              </div>
            </div>

            <div>
              <div className="itk-col-heading">Targets</div>
              <div className="itk-target-card">
                <div className="itk-target-lbl">Annual Revenue</div>
                <div className="itk-target-val">{viaCalcs ? fmtGBP(viaCalcs.revenue) : '—'}</div>
              </div>
              <div className="itk-target-card">
                <div className="itk-target-lbl">Revenue Per Customer</div>
                <div className="itk-target-val">{viaCalcs ? fmtGBP(viaCalcs.arpc) : '—'}</div>
              </div>
              <div className="itk-target-card">
                <div className="itk-target-lbl">Yearly Active Customers</div>
                <div className="itk-target-val">{viaCalcs ? fmtNum(viaCalcs.cust) : '—'}</div>
              </div>
            </div>
          </div>

          <div style={{ marginTop: 22 }}>
            <div className="itk-result-overline">Result</div>
            <div className={`itk-result-box ${viaCalcs ? 'itk-result-box--good' : ''}`} aria-live="polite">
              <span className="itk-result-icon">{viaCalcs ? YES_ICON : '?'}</span>
              <div>
                <div className="itk-result-title">
                  {viaCalcs ? <>You&rsquo;ve set your business targets</> : 'Confirm inputs'}
                </div>
                <span>
                  {viaCalcs
                    ? <>Don&rsquo;t worry if the number of customers you need seems high &mdash; you can scale up to that number over time. It could be an issue if you don&rsquo;t think there are that many customers out there.</>
                    : 'Add your retail price, profitability target and net profit target to set your targets.'}
                </span>
                {viaCalcs && (
                  <div className="itk-result-actions">
                    <Button pill onClick={() => navigate('/book')}>Book idea review</Button>
                    <Button variant="secondary" pill onClick={() => document.getElementById('itk-feasibility')?.scrollIntoView({ behavior: 'smooth' })}>
                      Continue to Project Feasibility
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* ── PROJECT FEASIBILITY ── */}
        <section className="itk-section-box" id="itk-feasibility">
          <div className="itk-overline">Section 3</div>
          <h2 className="itk-sec-title">Project Feasibility</h2>
          <h3 className="itk-card-title">Can you scale the product?</h3>
          <p className="itk-sec-desc">
            Use this section to review the feasibility of scaling your product sales with 10x &ldquo;hockey
            stick&rdquo; growth investors look for.
          </p>

          <div className="itk-two-col">
            <div>
              <div className="itk-field">
                <label className="itk-field-label" htmlFor="itk-inY3">Target year 3 customers</label>
                <input
                  className="itk-input itk-money"
                  id="itk-inY3"
                  inputMode="numeric"
                  autoComplete="off"
                  placeholder="e.g. 10,000"
                  value={feaY3 ? fmtNum(feaY3) : ''}
                  onChange={(e) => { setFeaY3(parseNum(e.target.value)); markDirty(); }}
                />
                {viaCalcs && !feaY3 && (
                  <div className="itk-target-working">
                    Suggested from your Business Viability section: {fmtNum(viaCalcs.cust)} customers
                  </div>
                )}
              </div>
            </div>
            <div>
              <GrowthGraph year3Target={effectiveY3} />
            </div>
          </div>

          {effectiveY3 && (
            <div style={{ marginTop: 22 }}>
              <div className="itk-result-overline">Result</div>
              <div className="itk-result-box itk-result-box--good" aria-live="polite">
                <span className="itk-result-icon">{YES_ICON}</span>
                <div>
                  <div className="itk-result-title">Does it make sense?</div>
                  <span>Don&rsquo;t forget our team of innovation experts are on hand to review your projects with you.</span>
                  <div className="itk-result-actions">
                    <Button pill onClick={() => navigate('/book')}>Book idea review</Button>
                    <Button variant="secondary" pill onClick={() => document.getElementById('itk-desirability')?.scrollIntoView({ behavior: 'smooth' })}>
                      Continue to Product Desirability
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </section>

        {/* ── PRODUCT DESIRABILITY ── */}
        <section className="itk-section-box" id="itk-desirability">
          <div className="itk-overline">Section 4</div>
          <h2 className="itk-sec-title">Product Desirability</h2>
          <p className="itk-sec-desc">
            It&rsquo;s a good indication your product could be desirable if you are able to answer these 4
            questions.
          </p>
          <div className="itk-desirability-fields">
            {DESIRABILITY_FIELDS.map((field) => (
              <div className="itk-desirability-block" key={field.key}>
                <label className="itk-desirability-title" htmlFor={`itk-des-${field.key}`}>{field.title}</label>
                <p className="itk-desirability-description">{field.description}</p>
                <textarea
                  id={`itk-des-${field.key}`}
                  className={`itk-desirability-input ${hasAnswer(desirability[field.key]) ? 'itk-desirability-input--complete' : ''}`.trim()}
                  value={desirability[field.key]}
                  onChange={(event) => { setDesirability((answers) => ({ ...answers, [field.key]: event.target.value })); markDirty(); }}
                  placeholder={field.placeholder}
                  rows={1}
                />
              </div>
            ))}
          </div>
        </section>

        {/* ── ASK COACH / NEXT STEP ── */}
        <section className="itk-section-box itk-coach-box" id="itk-coach">
          <div className="itk-overline">Next step</div>
          <h2 className="itk-sec-title">Book idea review</h2>
          <p className="itk-sec-desc itk-coach-desc">
            Arrange a free call with an experienced innovation coach to confidentially talk through your idea
            and map out the best route forward.
          </p>
          <div className="itk-coach-actions">
            <Button pill onClick={() => navigate('/book')}>Book idea review</Button>
            <Button variant="secondary" pill onClick={() => navigate('/learning')} className="itk-btn-on-turq">
              Go to learning hub
              <ArrowIcon />
            </Button>
          </div>
        </section>

        <div className="itk-back-top-wrap">
          <Button variant="secondary" pill onClick={() => document.getElementById('itk-dashboard')?.scrollIntoView({ behavior: 'smooth' })}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.4} strokeLinecap="round" strokeLinejoin="round">
              <line x1="12" y1="19" x2="12" y2="5" />
              <polyline points="5 12 12 5 19 12" />
            </svg>
            Back to the top
          </Button>
        </div>
      </div>

      {/* ── FLOATING ASK EXPERT FAB ── */}
      <button
        className="itk-fab-coach"
        onClick={() => document.getElementById('itk-coach')?.scrollIntoView({ behavior: 'smooth' })}
        aria-label="Ask expert"
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
        </svg>
        Ask expert
      </button>

      <div
        className={`itk-completion-modal ${completionOpen ? 'itk-completion-modal--open' : ''}`.trim()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="itk-completion-title"
        aria-hidden={!completionOpen}
      >
        <div className="itk-completion-card">
          <button type="button" className="itk-completion-close" onClick={closeCompletionModal} aria-label="Close">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.4} strokeLinecap="round">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
          <div className="itk-completion-tick" aria-hidden="true">
            <svg viewBox="0 0 72 72">
              <circle className="itk-completion-tick-circle" cx="36" cy="36" r="34" />
              <path className="itk-completion-tick-check" d="M21 37.5 31 47.5 51 26" />
            </svg>
          </div>
          <div className="itk-completion-burst" aria-hidden="true">
            {Array.from({ length: 14 }, (_, index) => <i key={index} />)}
          </div>
          <div id="itk-completion-title" className="itk-completion-title">Nice work!</div>
          <p className="itk-completion-subtitle">You completed the Idea Dashboard</p>
          <div className="itk-completion-actions">
            <Button
              pill
              autoFocus
              onClick={() => {
                closeCompletionModal();
                navigate('/book');
              }}
            >
              Discuss next steps
            </Button>
            <Button
              variant="secondary"
              pill
              onClick={() => {
                closeCompletionModal();
                navigate('/alerts');
              }}
            >
              Go to Alert Centre
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}

function ArrowIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.4} strokeLinecap="round" strokeLinejoin="round">
      <polyline points="9 18 15 12 9 6" />
    </svg>
  );
}

function parseNum(str: string): number | null {
  const n = parseInt(str.replace(/[^0-9]/g, ''), 10);
  return isNaN(n) ? null : n;
}